import * as vp from 'vopay';
import { CardTabs } from 'vopay';

import { FunctionComponent, useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import AccountContext from '../AccountContext';
import Details from '../Details';
import Billing from '../Billing/Billing';
import Users from '../Users/UsersContainer';
import TransactionsContainer from '../Transactions/TransactionsContainer';

import useStorage from '../../../hooks/useStorage';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';
import useFetchAgreementDetails from '../../../hooks/useApi/useFetchAgreementDetails';
import { useTranslation } from 'react-i18next';

import type { AccountsHandlers } from '../types';

type AccountProps = {
  transactions: vp.AccountTransactionsMap;
  handlers: AccountsHandlers;
};

type TabKey = 'billing' | 'details' | 'permissions' | 'transactions' | 'users';

type FC = FunctionComponent<AccountProps>;

const SubAccountTabs: FC = ({ transactions, handlers }: AccountProps) => {
  const { details } = useContext(AccountContext);
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const { canAccessBillingTab } = useModuleFeatureFlag('accounts');
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.SubAccount.Tabs' });

  const {
    accountToken: { storeAccountToken },
  } = useStorage();

  const { accountId } = useParams<{ accountId: string }>();
  const accountToken = details[accountId].token;
  const [agreementDetails, fetchAgreementDetails] = useFetchAgreementDetails({ bearer: accountToken });

  useEffect(() => {
    storeAccountToken(accountToken);
  }, [details, accountId, accountToken]);

  useEffect(() => {
    fetchAgreementDetails();
  }, []);

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabKey);
  };

  return (
    <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
      <CardTabs.List>
        <CardTabs.Trigger value="details">{t('details')}</CardTabs.Trigger>
        {canAccessBillingTab && <CardTabs.Trigger value="billing">{t('billing')}</CardTabs.Trigger>}
        <CardTabs.Trigger value="users">{t('users')}</CardTabs.Trigger>
        <CardTabs.Trigger value="transactions">{t('transactions')}</CardTabs.Trigger>
      </CardTabs.List>
      <CardTabs.Content value="details">
        <Details details={details} agreementDetails={agreementDetails} />
      </CardTabs.Content>
      <CardTabs.Content value="billing">
        <Billing />
      </CardTabs.Content>
      <CardTabs.Content value="users">
        <Users details={details} />
      </CardTabs.Content>
      <CardTabs.Content value="transactions">
        <TransactionsContainer defaultTransactions={transactions} details={details} handlers={handlers} />
      </CardTabs.Content>
    </CardTabs.Root>
  );
};

export default SubAccountTabs;
